import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { GHAAccountCugLeakSummary } from '~/services/ConnectionGoogleService';

import SectionLabel from './SectionLabel';
import { TIER_LABELS } from './constants';
import {
  formatLeakingNow,
  formatNeverLeakFooter,
  formatSmartLeakWindows,
  leakingTierLines,
  missingTimezoneCount,
} from './utils';

const SMART_RULE_TOOLTIP =
  'Inside these hours CUG rates go to every viewer. Outside them they still go to international ' +
  "viewers, while the hotel's home country sees B2C. Tier 1 and No tier never leak; Tier 4 leaks " +
  '24/7 — neither is affected by this schedule.';

const WHITELISTED_TOOLTIP =
  'Live properties on this tier carrying the CUG whitelist, out of all live properties on it. ' +
  'Without the whitelist a property never leaks, whatever its tier.';

const LEAKING_NOW_TOOLTIP =
  'An upper bound on what Google displays: a leaking property still serves B2C to everyone when ' +
  'its CUG rate is not cheaper than B2C.';

function StatusChip({ account }: { account: GHAAccountCugLeakSummary }) {
  if (account.enabled) {
    return <Chip size="small" variant="outlined" color="success" label="Enabled" />;
  }
  return (
    <Chip size="small" variant="outlined" label={account.configured ? 'Configured, disabled' : 'Not configured'} />
  );
}

/**
 * Which cug_tiers leak CUG rates to Google for this account, and what each is doing right now.
 * Rows cover only the tiers that can leak; the tiers that never leak collapse into the footer,
 * since repeating "never leaks" per row costs a line each and says nothing new.
 */
export default function CugLeakingSection({ account }: { account: GHAAccountCugLeakSummary }) {
  const rows = leakingTierLines(account.byTier);
  const neverLeakFooter = formatNeverLeakFooter(account.byTier);
  const missingTimezone = missingTimezoneCount(account.byTier);

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 0.5 }}
      >
        <SectionLabel>CUG rate leaking</SectionLabel>
        <StatusChip account={account} />
      </Stack>

      {account.smartLeakWindows.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Tooltip title={SMART_RULE_TOOLTIP}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: 'fit-content', cursor: 'help' }}>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Smart CUG leaking rule · Tier 2/3 only
              </Typography>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            </Stack>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {formatSmartLeakWindows(account.smartLeakWindows)} · property-local time
          </Typography>
        </Box>
      )}

      {rows.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 90 }}>CUG tier</TableCell>
              <TableCell align="right" sx={{ width: 150, whiteSpace: 'nowrap' }}>
                <Tooltip title={WHITELISTED_TOOLTIP}>
                  <span>Whitelisted (live)</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ pl: 10 }}>
                <Tooltip title={LEAKING_NOW_TOOLTIP}>
                  <span>CUG leaking now</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((line) => (
              <TableRow key={line.cugTier} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell>{TIER_LABELS[line.cugTier] ?? line.cugTier}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {line.whitelisted.toLocaleString()} / {line.liveTotal.toLocaleString()}
                </TableCell>
                <TableCell sx={{ pl: 10, fontVariantNumeric: 'tabular-nums' }}>
                  {formatLeakingNow(line, account.enabled)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No properties on a tier that can leak CUG rates.
        </Typography>
      )}

      {neverLeakFooter != null && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          {neverLeakFooter}
        </Typography>
      )}

      {account.enabled && missingTimezone > 0 && (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
          <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="warning.main">
            {missingTimezone.toLocaleString()} whitelisted Tier 2/3 have no timezone, so they never leak
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
